import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from "@angular/core";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {CustomerControllerService, GetCustomerRequest} from "skyhopper-api";
import {ApplicationService} from "../../application.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {Router} from "@angular/router";
import {NGXLogger} from "ngx-logger";
import {UtilitiesService} from "../../../core/services/utilities.service";
import {MatDialog} from "@angular/material/dialog";
import {FacadeService} from "../../../shared/services/facade.service";
import {State} from "../../../shared/models/state";
import {TellUsAboutYourselfData} from "../../../shared/models/form-data";
import {ProductControllerService as FalconProductControllerService} from "falcon-api";
import {ValidationDetail} from "../../../shared/models/ValidationDetail";
import {OnboardingRequest} from "../../../shared/models/onboarding-request.model";
import {Subject} from "rxjs";
import {MatRadioChange} from "@angular/material/radio";
import {Options} from 'ngx-google-places-autocomplete/objects/options/options';
import {CreateApplicationService} from "../create-application.service";
import {AppValidator} from "../../../shared/interfaces/AppValidator";
import {take, takeUntil} from "rxjs/operators";
import {PRIMARY_APPLICANT_LABEL, PRODUCT_MINOR_SAVINGS} from "../../../shared/constants";
import {Products} from "../../../shared/models/products";
import {CustomValidators} from "../../../shared/validators/custom-validator";
import {AuthService} from "src/app/core/auth/auth.service";
import {Permissions} from "src/app/core/auth/permissions";


@Component({
  selector: 'app-tell-us-about-yourself',
  templateUrl: './tell-us-about-yourself.component.html',
  styleUrls: ['./tell-us-about-yourself.component.scss']
})
export class TellUsAboutYourselfComponent implements OnInit, AppValidator, OnDestroy, OnChanges {
  @Input() formGroup!: FormGroup;
  @Input() getPrimaryTellUsAboutYourselfData!: () => TellUsAboutYourselfData;
  @Input() selectedProduct!: any;
  hideSsn: boolean = true;
  maxDate = new Date();
  minDate = new Date();
  validationDetail: ValidationDetail;
  onboardingRequest: OnboardingRequest = new OnboardingRequest();
  usePrimaryAddress: boolean = false;
  secondaryInitialAddressLine1: string = "";
  secondaryInitialAddressLine2: string = "";
  secondaryInitialCity: string = "";
  secondaryInitialState: any = "";
  secondaryInitialZipCode: string = "";
  private _destroySubject$: Subject<void> = new Subject();
  isEmployeeProduct: boolean = false;
  maskSsn: boolean = false;
  stateList: State[] = [];

  private emailValidators = [Validators.required, Validators.email, Validators.minLength(2), Validators.maxLength(100), CustomValidators.emailValidator];

  //google address related
  options = {
    types: ['address'],
    componentRestrictions: {country: ['us']}
  } as unknown as Options;

  get isPrimary(): boolean {
    return this.formGroup.value.isPrimary;
  }

  constructor(private formBuilder: FormBuilder,
              private falconProductService: FalconProductControllerService,
              private applicationService: ApplicationService,
              private snackBar: MatSnackBar,
              private router: Router,
              private logger: NGXLogger,
              private customerControllerService: CustomerControllerService,
              private utils: UtilitiesService,
              public dialog: MatDialog,
              public facadeService: FacadeService,
              private createApplicationService: CreateApplicationService,
              private authService: AuthService) {
    this.validationDetail = new ValidationDetail(this.formGroup, new Map<string, string>());
  }

  async ngOnInit() {
    let accessPermissionList: string[] = [];
    this.formGroup.value.isPrimary = this.isPrimary;
    this.setupValidationMap();
    this.setPageDefaults();
    this.setupStatusTracker();
    this.setupValidationTrigger();
    await this.facadeService.setStateList();
    this.setStateList();

    //set copyaddressfrom primary -false on component load
    this.onboardingRequest = this.applicationService.onboardingRequest;
    this.usePrimaryAddress = this.onboardingRequest.copyAddressFromPrimary ?? false;
    this.formGroup.valueChanges.pipe(takeUntil(this._destroySubject$)).subscribe(value => {
      this.createApplicationService.formElementHasChanged = true;
    });

    this.authService.role.pipe(take(1)).subscribe(role => {
      accessPermissionList = this.authService.getAccessPermissions(role);

      if (accessPermissionList.includes(Permissions.Type.VIEW_APPLICATION_UNMASKED)) {
        this.maskSsn = true
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedProduct.currentValue === Products.EmployeeChecking || changes.selectedProduct.currentValue === Products.EmployeeSavings) {
      this.isEmployeeProduct = true;
      this.formGroup.get("emailAddress")?.setValidators(this.emailValidators.concat(CustomValidators.emailValidator));
      this.validationDetail.labels.set("emailAddress", `${this.formGroup.value.applicantType} Personal Email`)

    } else {
      this.isEmployeeProduct = false;
      this.formGroup.get("emailAddress")?.setValidators(this.emailValidators);

      this.validationDetail.labels.set("emailAddress", `${this.formGroup.value.applicantType} Email`)
    }
    this.formGroup.get("emailAddress")?.updateValueAndValidity({onlySelf: true, emitEvent: false})
    this.updateValidations();
  }

  ngOnDestroy(): void {
    this._destroySubject$.next();
    if (!this.formGroup.value.isPrimary) {
      this.clearValidations();
    }
  }

  /**
   * Setup validation map for labels.
   */
  setupValidationMap(): void {
    if (this.formGroup.value.applicantType === 'undefined' || this.formGroup.value.applicantType === null) {
      this.formGroup.value.applicantType = PRIMARY_APPLICANT_LABEL
    }

    // Setup labels for validation
    const {applicantType} = this.formGroup.value;
    let labelMap = new Map<string, string>();
    labelMap.set("firstName", `${applicantType} First Name`);
    labelMap.set("middleName", `${applicantType} Middle Name`);
    labelMap.set("lastName", `${applicantType} Last Name`);
    labelMap.set("suffix", `${applicantType} Suffix`);
    labelMap.set("preferredName", `${applicantType} Preferred Name`);
    labelMap.set("ssnNumber", `${applicantType} Social Security Number`);
    labelMap.set("birthDate", `${applicantType} Date of Birth`);
    labelMap.set("addressLine1", `${applicantType} Address Line 1`);
    labelMap.set("addressLine2", `${applicantType} Address Line 2`);
    labelMap.set("city", `${applicantType} City`);
    labelMap.set("state", `${applicantType} State`);
    labelMap.set("zipCode", `${applicantType} Zip`);
    labelMap.set("phoneNumber", `${applicantType} Phone Number`);
    labelMap.set("emailAddress", `${applicantType} Email`);
    this.formGroup.get("emailAddress")?.setValidators(this.emailValidators.concat(CustomValidators.emailValidator));
    this.validationDetail = new ValidationDetail(this.formGroup, labelMap);
  }

  /**
   * Set page defaults
   */
  setPageDefaults(): void {
    // set the minimum on the birthday control
    let nowDate = new Date();
    const applicationProduct = this.applicationService.applicationLogsRequest.products?.find(product => !!product);
    if (this.formGroup.value.isPrimary && applicationProduct?.productDescription?.trim() === PRODUCT_MINOR_SAVINGS) {
      this.maxDate = nowDate;
      this.minDate = new Date(nowDate.getFullYear() - 18, nowDate.getMonth(), nowDate.getDate());
    } else {
      this.maxDate = new Date(nowDate.getFullYear() - 18, nowDate.getMonth(), nowDate.getDate());
      this.minDate = new Date(1900, 1, 1);
    }
  }

  /***
   *  Get status changes for form and update status tracker.
   */
  setupStatusTracker(): void {
    this.formGroup.statusChanges.pipe(takeUntil(this._destroySubject$)).subscribe(state => {
      const isFormDirty = this.formGroup.dirty;
      this.logger.debug("Tell us about yourself, Status Change:" + state, " touched:" + isFormDirty);
      this.updateValidations();
    });
  }

  setupValidationTrigger(): void {
    this.createApplicationService.onTriggerValidations.pipe(takeUntil(this._destroySubject$)).subscribe(() => {
      this.formGroup.markAllAsTouched();
      this.formGroup.updateValueAndValidity();
    });
  };

  /**
   * Call to update validations in the validations component.
   */
  updateValidations(): void {
    this.createApplicationService.validationsChange.emit(this.validationDetail);
  }

  clearValidations(): void {
    this.createApplicationService.validationsChange.emit(
      new ValidationDetail(this.formGroup, this.validationDetail.labels, true)
    );
  }

  //function to include slashes automatically
  onDateKeyUp(event: KeyboardEvent) {
    let element = event.currentTarget as HTMLInputElement
    const value = element.value;
    if (event.keyCode != 8) { //skip backspace event
      if (value.length == 2) {
        element.value = value + '/';
      } else if (value.length == 5) {
        element.value = value + '/';
      }
    }
  }

  //function to validate SSN
  validateSSN(event: any) {
    if ((event.keyCode < 48 || event.keyCode > 57)) {
      event.preventDefault();
      return false;
    } else {
      return true;
    }
  }

  /**
   * copy/set Primary applicant address to the secondary applicant
   * @param event
   */
  onUsePrimaryAddress(event: MatRadioChange): void {
    //onboardingRequest object is empty. so setting the values again.
    this.onboardingRequest = this.onboardingRequest;
    this.onboardingRequest.copyAddressFromPrimary = this.usePrimaryAddress = event.value;

    if (this.usePrimaryAddress) {
      this.secondaryInitialAddressLine1 = this.formGroup.controls['addressLine1'].value;
      this.secondaryInitialAddressLine2 = this.formGroup.controls['addressLine2'].value;
      this.secondaryInitialCity = this.formGroup.controls['city'].value;
      this.secondaryInitialState = this.formGroup.controls['state'].value;
      this.secondaryInitialZipCode = this.formGroup.controls['zipCode'].value;
    }

    this.applicationService.onboardingRequest = this.onboardingRequest;
    const setFormValue = (name: string, value: any): void => {
      const control = this.formGroup.controls[name];
      control.setValue(value);
      control.markAsTouched();
    }

    if (this.usePrimaryAddress) {
      var {addressLine1, addressLine2, city, state, zipCode} = this.getPrimaryTellUsAboutYourselfData();
    } else {
      var addressLine1 = this.secondaryInitialAddressLine1;
      var addressLine2 = this.secondaryInitialAddressLine2;
      var city = this.secondaryInitialCity;
      var state = this.secondaryInitialState;
      var zipCode = this.secondaryInitialZipCode;
    }

    setFormValue('addressLine1', addressLine1);
    setFormValue('addressLine2', addressLine2);
    setFormValue('city', city);
    setFormValue('state', state);
    setFormValue('zipCode', zipCode);
    this.formGroup.updateValueAndValidity();
  }

  //get the field label and set the validation
  getFieldError(key: string): any {
    return this.validationDetail.labels.get(key);
  }

  compareValueWithSelection(optionValue: any, selectionValue: any): boolean {
    return optionValue === selectionValue;
  }

  onValidateClicked() {
    const {addressLine1, addressLine2, city, state, zipCode} = this.formGroup.controls;

    this.applicationService.addressVerify({
      streetAddress: addressLine1.value,
      streetAddress1: addressLine2.value,
      city: city.value,
      state: state.value,
      zip: zipCode.value
    }).subscribe(uspsAddress => {
      const verifiedAddress = uspsAddress.address!;
      const {streetAddress, streetAddress1, city: vCity, state: vState, zip} = verifiedAddress;

      addressLine1.setValue(streetAddress!);
      addressLine2.setValue(streetAddress1!);
      city.setValue(vCity!);
      state.setValue(this.stateList.find(state => state.stateAbbr === vState!)!.id);
      zipCode.setValue(zip!);
    }, error => {
    });
  }

  hideEye() {
    setTimeout(() => {
      if (!this.hideSsn) this.hideSsn = true;
    }, 5000);
  }

  private setStateList() {
    const loadedStates = JSON.parse(this.facadeService.getStorageItem("states"));

    this.stateList = loadedStates;
    this.facadeService.stateList = loadedStates;
  }
}
